import React, { useCallback, useContext } from 'react';
import ReactTextareaAutosize from 'react-textarea-autosize';
import classNames from 'classnames';
import {
	IChatMessagesOut,
	IChatTextMessage,
	MessageType,
} from '@enuan/chat__utils';
import { formMessages } from '../i18n';
import { useIntl } from 'react-intl';
import { submitContext } from '../../../contexts/submit.context';
import { useLogError } from '../../ErrorBoundary/useLogError';
import { styled } from '@mui/material/styles';
import { scrollbar } from 'src/theme/mixins';
import { useAttachMedia } from '../attach/hooks';

/**
 * Auto-resizing textarea for composing text messages.
 *
 * In addition to standard text input, intercepts clipboard paste events
 * containing files (e.g. images or documents copied from the OS) and routes
 * them through {@link useAttachMedia} so they follow the same validation and
 * preview pipeline as files selected via the Attach modal.
 */
export const Textarea = styled(
	({ className }: { className?: string }) => {
		const intl = useIntl();
		const placeholder = intl.formatMessage(formMessages.placeholder);
		const { message, setMessage, setAction, submitForm } =
			useContext(submitContext);

		const logError = useLogError('Textarea');
		const { attachFile, canAttachMedia } = useAttachMedia();

		const onChangeCallback = useCallback(
			({
				currentTarget: { value: text },
			}: React.FormEvent<HTMLTextAreaElement>) => {
				if (!text.trimStart()) {
					setMessage(undefined);
					setAction(undefined);
				} else {
					const msg: IChatMessagesOut<IChatTextMessage> = {
						_type: MessageType.TEXT,
						_data: text,
					};
					setMessage(msg);
					setAction('texting');
				}
			},
			[setAction, setMessage],
		);

		const onKeyDown = useCallback<
			React.KeyboardEventHandler<HTMLTextAreaElement>
		>(
			(e) => {
				const text = e.currentTarget.value;
				if (e.key === 'Enter' && !e.shiftKey) {
					e.preventDefault();
					if (text.trimStart()) {
						const msg: IChatMessagesOut<IChatTextMessage> = {
							_type: MessageType.TEXT,
							_data: text,
						};
						submitForm(msg).catch((error) =>
							logError('onEnterKeyDown', { error, payload: { message: msg } }),
						);
					}
				}
			},
			[submitForm, logError],
		);

		/**
		 * Intercepts paste events that carry files from the OS clipboard.
		 *
		 * When `canAttachMedia` is true and the clipboard contains at least one
		 * file, the default browser paste behaviour is suppressed and the first
		 * file is forwarded to `attachFile` which validates and sets it as the
		 * pending media message.  Plain-text paste falls through to the default
		 * browser handling so the textarea content is updated normally.
		 */
		const onPaste = useCallback(
			(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
				if (!canAttachMedia) return;
				if (e.clipboardData.files.length === 0) return;

				e.preventDefault();
				attachFile(e.clipboardData.files[0]).catch((error) =>
					logError('onPaste', { error }),
				);
			},
			[canAttachMedia, attachFile, logError],
		);

		return (
			<ReactTextareaAutosize
				placeholder={placeholder}
				onChange={onChangeCallback}
				onKeyDown={onKeyDown}
				onPaste={onPaste}
				className={classNames('textarea', className)}
				value={message?._type === MessageType.TEXT ? message._data : ''}
			/>
		);
	},
	{ label: 'Textarea', target: 'Textarea-root' },
)({
	whiteSpace: 'pre-wrap',
	flex: 1,
	resize: 'none',
	backgroundColor: 'transparent',
	outline: 'none',
	border: 0,
	maxHeight: '100%',
	padding: 0,
	fontFamily: "'Roboto', sans-serif",
	fontSize: '1.5em',
	fontWeight: 400,
	color: 'var(--chat__footer_color)',
	'&::placeholder': {
		color: 'var(--chat__footer__form__placeholder_color)',
	},
	...scrollbar({ visible: true }),
});
